import React, { Suspense, useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Typography,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import {
  ArrowBack,
  DirectionsBus,
  Route,
  LocalGasStation,
  Receipt,
  Assignment,
  LocalShipping,
} from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
import { useToast } from "../../components/Toast";

// Lazy load components
const ContractInformation = React.lazy(
  () => import("./components/ContractInformation")
);
const IncomeDetails = React.lazy(() => import("./components/IncomeDetails"));
const FixedExpense = React.lazy(() => import("./components/FixedExpense"));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`trip-tabpanel-${index}`}
      aria-labelledby={`trip-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 2 }}>
          <Suspense fallback={<Loading />}>{children}</Suspense>
        </Box>
      )}
    </div>
  );
}

const tabsConfig = [
  {
    label: "Contract Information",
    icon: <DirectionsBus sx={{ fontSize: 16 }} />,
    path: "contract-information",
    component: ContractInformation,
    description: "Select vehicle and set contract details",
  },
  {
    label: "Income Details",
    icon: <Receipt sx={{ fontSize: 16 }} />,
    path: "income-details",
    component: IncomeDetails,
    description: "Income details",
  },
  {
    label: "Expense Details",
    icon: <LocalGasStation sx={{ fontSize: 16 }} />,
    path: "expense-details",
    component: FixedExpense,
    description: "Expense details",
  },
];

const FixedIncomeForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, loading: authLoading } = useFirebaseAuth();
  const { showToast } = useToast();

  const [currentTab, setCurrentTab] = useState(0);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [contractData, setContractData] = useState<any>(null);
  const [incomeData, setIncomeData] = useState<any>(null);
  const [expenseData, setExpenseData] = useState<any>(null);
  const [incomeSummary, setIncomeSummary] = useState<any>(null);
  const [fixedJourneyId, setFixedJourneyId] = useState<any>(null);

  // Completion status for each tab
  const [tabCompletionStatus, setTabCompletionStatus] = useState<{
    [key: number]: boolean;
  }>({
    0: false, // Vehicle & Route
    1: false, // Income
    2: false, // Expense
  });

  // Sync tab with URL path
  useEffect(() => {
    const pathSegments = location.pathname.split("/");
    const tabPath = pathSegments[pathSegments.length - 1];

    // If on the base route without a specific tab, redirect to vehicle-route tab
    if (tabPath === "fixed-income") {
      navigate(`${location.pathname}/contract-information`, { replace: true });
      return;
    }

    const tabIndex = tabsConfig.findIndex((tab) => tab.path === tabPath);
    if (tabIndex !== -1 && tabIndex !== currentTab) {
      setCurrentTab(tabIndex);
    }
  }, [location.pathname, currentTab, navigate]);

  // Update completion status based on trip data
  useEffect(() => {
    setTabCompletionStatus({
      0: !!contractData,
      1: !!incomeData,
      2: !!expenseData,
    });
  }, [contractData, incomeData, expenseData]);

  // Check if user can navigate to a specific tab
  const canNavigateToTab = (tabIndex: number) => {
    if (tabIndex === 0) return true; // Vehicle & Route selection always accessible
    if (tabIndex === 1) return !!contractData; // Income needs vehicle
    if (tabIndex === 2) return !!incomeData; // Expense needs vehicle
    return false;
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    const tabPath = tabsConfig[newValue].path;
    navigate(`/fixed-income/${tabPath}/${id ? id : ""}`, {
      replace: true,
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleVehicleSelect = (vehicle: any) => {
    setSelectedVehicle(vehicle);
  };

  const handleVehicleRouteNext = (id?: string) => {
    if (id) {
      navigate(`/fixed-income/income-details/${id}`, {
        replace: true,
      });
      setCurrentTab(1);
      return;
    }
    handleTabChange(null as any, 1);
  };

  if (authLoading) {
    return <Loading />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {/* Header Section */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <IconButton
            onClick={handleBack}
            size="small"
            sx={{ mr: 1, color: "primary.main" }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: isMobile ? "1rem" : "1.1rem",
              color: "primary.main",
            }}
          >
            Contract Income
          </Typography>
        </Box>

        <Breadcrumbs
          separator="›"
          sx={{
            fontSize: "0.75rem",
            "& .MuiBreadcrumbs-separator": { mx: 0.5 },
          }}
        >
          <Link
            underline="hover"
            color="inherit"
            href="/dashboard"
            sx={{ fontSize: "0.75rem" }}
          >
            Dashboard
          </Link>
          <Typography sx={{ fontSize: "0.75rem", color: "text.primary" }}>
            Contract Income
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Progress Indicator */}
      {selectedVehicle && (
        <Card
          sx={{
            mb: 2,
            backgroundColor: "grey.100",
            borderLeft: "4px solid",
            borderLeftColor: "primary.main",
          }}
        >
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <DirectionsBus sx={{ fontSize: 18, color: "primary.main" }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                {selectedVehicle.vehicleNumber}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", opacity: 0.8 }}
              >
                {selectedVehicle.vehicleType} • {selectedVehicle.bodyType}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Main Form Container */}
      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          minHeight: "70vh",
        }}
      >
        {/* Tabs */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: "grey.50",
          }}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons="auto"
            sx={{
              minHeight: 42,
              "& .MuiTab-root": {
                minHeight: 42,
                fontSize: "0.75rem",
                fontWeight: 500,
                textTransform: "none",
                px: isMobile ? 1 : 2,
                py: 1,
                gap: 0.5,
                opacity: 1,
              },
              "& .MuiTabs-indicator": {
                height: 3,
              },
            }}
          >
            {tabsConfig.map((tab, index) => {
              const canAccess = canNavigateToTab(index);
              const isCompleted = tabCompletionStatus[index];

              return (
                <Tab
                  key={tab.path}
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {tab.label}
                      {isCompleted && (
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: "success.main",
                          }}
                        />
                      )}
                    </Box>
                  }
                  icon={tab.icon}
                  iconPosition="start"
                  disabled={!canAccess}
                  sx={{
                    opacity: canAccess ? 1 : 0.4,
                    "& .MuiTab-iconWrapper": {
                      color: isCompleted ? "success.main" : "inherit",
                    },
                  }}
                />
              );
            })}
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ minHeight: "50vh" }}>
          {tabsConfig.map((tab, index) => (
            <TabPanel key={tab.path} value={currentTab} index={index}>
              {index === 0 ? (
                // Special handling for Vehicle & Route selection
                <ContractInformation
                  selectedVehicle={selectedVehicle}
                  onVehicleSelect={handleVehicleSelect}
                  onNext={handleVehicleRouteNext}
                  setFixedJourneyId={setFixedJourneyId}
                  contractData={contractData}
                  setContractData={setContractData}
                />
              ) : index === 1 ? (
                // Special handling for Income Details
                <IncomeDetails
                  onNext={() => {
                    const nextTab = Math.min(index + 1, tabsConfig.length - 1);
                    handleTabChange(null as any, nextTab);
                  }}
                  setIncomeSummary={setIncomeSummary}
                  fixedJourneyId={fixedJourneyId}
                  incomeData={incomeData}
                  setIncomeData={setIncomeData}
                  contractDetails={contractData}
                />
              ) : index === 2 ? (
                // Special handling for Expense Details
                <FixedExpense
                  selectedVehicle={selectedVehicle}
                  fixedJourneyId={fixedJourneyId}
                  setExpenseData={setExpenseData}
                />
              ) : null}
            </TabPanel>
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

export default FixedIncomeForm;
